import React, { useContext as useContextCore, useCallback, useState } from 'react'

export type AddressEditPopOverContextType = {
  openKey: string | null
  open: (key: string) => void
  close: () => void
  /** `true` only inside a mounted `AddressEditPopOverProvider`. With no Provider above it — e.g.
   * the storefront's `OrderInlineInput` tree, which never mounts one — the context reads back the
   * bare `{}` default below, so this destructures to `undefined` (falsy): `AddressEditField` uses
   * that to detect the absence and force the self-contained INLINE arm, which never calls
   * `open`/`close`, instead of dispatching into a coordinator nothing provided. */
  hasProvider: boolean
}

// Empty assertion, never a default carrying real data (`webui-component-style-guide.md` →
// "Components with Context"). `hasProvider` (and `open`/`close`) read back `undefined` from this,
// which is exactly the falsy signal `AddressEditField` needs — see the type's doc comment above.
const context = React.createContext<AddressEditPopOverContextType>({} as AddressEditPopOverContextType)

export type AddressEditPopOverProviderProps = { children: React.ReactNode }

export const AddressEditPopOverProvider = ({ children }: AddressEditPopOverProviderProps) => {
  const [openKey, setOpenKey] = useState<string | null>(null)

  // `open` replaces the current key rather than adding to a set, so opening a second address
  // editor closes any other that is open by construction.
  const open = useCallback((key: string) => setOpenKey(key), [])
  const close = useCallback(() => setOpenKey(null), [])

  return <context.Provider value={{ openKey, open, close, hasProvider: true }}>{children}</context.Provider>
}

export const useAddressEditPopOverContext = () => useContextCore(context)
